import type { Ref } from 'vue'
import { computed } from 'vue'

import type { DiagnosisSummary, RoomID } from '@/types/app'
import { useKarte } from '../../hooks/chatView/useKarte/useKarte'
import { useMatch } from '../../hooks/chatView/useMatch/useMatch'
import {
  useChatRoomQuery,
  useDiagnosisByUserQuery,
} from './useOwnChatRoom.generated'
import { parseDiagnosisByUserResponseToDiagnosisSummary } from '@/apis/repositories/parsers/diagnosis.ts'

/*
 * -----------------------
 * ChatRoom List用のMatch
 * -----------------------
 */
export const useOwnChatRoom = (roomId: Ref<Maybe<RoomID>>) => {
  const {
    match,
    refetch: matchRefetch,
    loading: matchLoading,
    error: matchError,
  } = useMatch(roomId, 'own')
  const { karte, loading: karteLoading } = useKarte(match)

  const {
    result,
    loading: chatRoomLoading,
    refetch,
  } = useChatRoomQuery(
    computed(() => ({
      // enabled で制御されているので、'' になることはない
      id: roomId.value?.toString() ?? '',
    })),
    () => ({
      enabled: roomId.value != null,
    })
  )

  const { result: diagnosisQuery, loading: diagnosisQueryLoading } =
    useDiagnosisByUserQuery(
      computed(() => ({ userId: match.value?.user?.id?.toString() ?? '' })),
      () => ({ enabled: !!match.value?.user?.id })
    )

  const diagnosisSummary = computed<DiagnosisSummary | undefined>(() => {
    const diagnosisByUser = diagnosisQuery.value?.diagnosisByUser
    return diagnosisByUser
      ? parseDiagnosisByUserResponseToDiagnosisSummary(diagnosisByUser)
      : undefined
  })

  const chatRoom = computed(() => {
    return result.value?.ownChatRoom ?? undefined
  })

  const viewer = computed(() => {
    return result.value?.viewer
  })

  const loading = computed(
    () =>
      matchLoading.value ||
      diagnosisQueryLoading.value ||
      karteLoading.value ||
      chatRoomLoading.value
  )

  return {
    refetch,
    loading,
    matchError,
    chatRoom,
    match,
    matchRefetch,
    diagnosisSummary,
    karte,
    viewer,
  }
}
